/**
 * Next.js client-router shim (delete with PROD-8714 / Vite migration).
 *
 * ## Two routers, one app
 *
 * PROD-8478 moved **product routing** to React Router 7 (`routing/routes.tsx`,
 * `useAppRouter`). Next.js Pages is still the build shell until Vite lands: nginx
 * serves `static-site-router` HTML on every full-page load while the browser URL
 * is the real route (e.g. `/auth/account/sign-up/`).
 *
 * RR7 reads that URL once `RouterProvider` mounts. Separately, the Next.js client
 * runtime hydrates against the exported `static-site-router` page. If we do not
 * soft-hand it off via `Router.push(asPath)`, Next tries a hard reload to the URL
 * already in the address bar → `Invariant: attempted to hard navigate to the same
 * URL` (PROD-9089). This module is that hand-off only; it does not route the UI.
 *
 * Requires exported page chunks for the target URL (auth paths via
 * `pages/[...frontegg-router].tsx`, everything else via normal page exports).
 *
 * @see pages/_app.tsx — runs this before mounting RR7
 * @see pages/static-site-router.tsx — HTML entry only; bootstrap lives in `_app`
 */
import Router from 'next/router';
import { getAppBasename } from '../../hooks/useAppRouter/getAppBasename';
import {
  prepareSideloadFromAsPath,
  redirectAfterSideloadPrepare,
} from '../../features/extensions/sideloading';
import { shouldRouteClientSide } from '../../utils/shouldRouteClientSide';

type BuildManifest = {
  __rewrites?: { beforeFiles?: { destination?: string }[] };
};

/** Dev catch-all rewrite loops if left in `__BUILD_MANIFEST` when we `Router.push`. */
const stripStaticSiteRouterRewriteFromManifest = (): void => {
  const manifest = (window as Window & { __BUILD_MANIFEST?: BuildManifest })
    .__BUILD_MANIFEST;
  const beforeFiles = manifest?.__rewrites?.beforeFiles;
  if (!beforeFiles || !manifest?.__rewrites) {
    return;
  }

  const entryDestination = `${getAppBasename()}${Router.pathname}`;
  manifest.__rewrites.beforeFiles = beforeFiles.filter(
    (rewrite) => rewrite.destination !== entryDestination,
  );
};

/**
 * Soft-sync Next's internal route to the browser URL after a static-site-router
 * entry load. No-op when Next already matches (client navigations, direct page hits).
 */
export const bootstrapNextRouterFromStaticSiteEntry =
  async (): Promise<void> => {
    if (typeof window === 'undefined') {
      return;
    }

    const { asPath, pathname } = Router;
    if (!shouldRouteClientSide(asPath, pathname)) {
      return;
    }

    stripStaticSiteRouterRewriteFromManifest();

    const sideloadPrepare = prepareSideloadFromAsPath(asPath);
    if (sideloadPrepare) {
      redirectAfterSideloadPrepare(sideloadPrepare);
      return;
    }

    await Router.push(asPath);
  };
